/**
 * UI store — chrome state and the shared toast queue.
 *
 * Spec section 10's definition of done: "Every user action produces a visible
 * success or failure state using the shared toast utility." `FND-10` was to ship
 * that utility in `packages/ui`; it shipped tokens, generators and CI gates only,
 * and `packages/ui/components` does not exist at all. This is that utility,
 * living in `apps/web` until `FND-10`'s component set lands — at which point it
 * moves to `packages/ui` unchanged, because nothing here is app-specific.
 *
 * Toasts are for the *outcome of an action the user took*. A failed background
 * read is an inline error state, not a toast: a toast that appears while nobody
 * is looking at it has told nobody anything.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastTone = 'positive' | 'caution' | 'critical'

export interface Toast {
  readonly id: number
  readonly tone: ToastTone
  readonly message: string
  /** Optional second line. Used for "what happens next", never for a stack trace. */
  readonly detail?: string
}

/** Long enough to read a sentence twice. Criticals do not auto-dismiss at all. */
const DISMISS_AFTER_MS = 6000

const NAV_STORAGE_KEY = 'match-point.nav-expanded'

export const useUiStore = defineStore('ui', () => {
  const toasts = ref<Toast[]>([])
  let nextId = 1

  /**
   * Rail expansion at tablet width. Browser-local rather than per-account: it is
   * a property of the window you are in, and syncing it across devices would
   * mean a phone decides how a desktop renders.
   */
  const navExpanded = ref(readNavExpanded())

  function readNavExpanded (): boolean {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem(NAV_STORAGE_KEY) === 'true'
  }

  function setNavExpanded (value: boolean): void {
    navExpanded.value = value
    if (typeof localStorage !== 'undefined') localStorage.setItem(NAV_STORAGE_KEY, String(value))
  }

  function dismiss (id: number): void {
    toasts.value = toasts.value.filter(toast => toast.id !== id)
  }

  function toast (tone: ToastTone, message: string, detail?: string): number {
    const id = nextId++
    toasts.value = [...toasts.value, detail === undefined ? { id, tone, message } : { id, tone, message, detail }]
    // A critical outcome stays until acknowledged. Auto-dismissing "your referral
    // was not sent" is how a user comes to believe it was.
    if (tone !== 'critical') setTimeout(() => dismiss(id), DISMISS_AFTER_MS)
    return id
  }

  return {
    toasts,
    navExpanded,
    setNavExpanded,
    dismiss,
    toast,
    succeeded: (message: string, detail?: string) => toast('positive', message, detail),
    warned: (message: string, detail?: string) => toast('caution', message, detail),
    failed: (message: string, detail?: string) => toast('critical', message, detail)
  }
})
